import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from "react";
import ReactPaginate from "react-paginate";
import { useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import Modal from "../Modal";
import {
  getFormData,
  getSingleReporting,
} from "../../services/dashboardService";
import UpdateEpidemic, { updateEpidemicTypes } from "./update-epidemic";
import UpdateIncidents, {
  updateIncidentsInputeTypes,
} from "./update-incidents";
import UpdateHealthEvents, {
  updateHealthInputsTypes,
} from "./update-health-event";
import UpdateSafetyEvents, { updateSafetyTypes } from "./update-safefy-events";
import UpdateFoodSafety, { updateFoodsafetyTypes } from "./update-food-safety";
import getData from "../../services/dashboardService";
import { formatDate } from "../../helpers";
import UpdateNothingToDeclare, {
  updateNothingToDeclareTypes,
} from "./update-nothing-to-declare";
import { getMonitoringJwt, permit } from "../../services/authService";
import EditButton from "../misc/EditButton";
import EyeButton from "../misc/EyeButton";

// Component to fetch health event security level directly from the API
const HealthEventLevel = ({ eventId, forceRefresh }: { eventId: number, forceRefresh: number }) => {
  const [level, setLevel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  const fetchSecurityLevel = useCallback(async () => {
    try {
      const res = await getFormData('health-events', eventId);
      if (res && res.data && res.data.securityLevel) {
        setLevel(res.data.securityLevel);
      } else {
        setLevel(null);
      }
    } catch (error) {
      console.error(`Error fetching security level for event ${eventId}:`, error);
      setLevel(null);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Fetch data when component mounts or when forceRefresh changes
  useEffect(() => {
    fetchSecurityLevel();
  }, [fetchSecurityLevel, forceRefresh]);

  if (loading) {
    return <span>...</span>;
  }

  return level ? (
    <span>
      <Trans i18nKey="level">Level</Trans> {level}
    </span>
  ) : (
    <span>-</span>
  );
};

interface Arr {
  _id: string;
  period: string;
  perimeter: string;
  institutionId: string;
  event: string;
  status: string;
  eventId: number;
  eventDate: string;
  securityLevel: string;
}

interface TableProps {
  Data: Arr[];
  setLoading: Dispatch<SetStateAction<boolean>>;
  total: number;
  setData: Dispatch<SetStateAction<Arr[]>>;
  setTotal: Dispatch<SetStateAction<number>>;
  perimeter: string;
  institutionId: string;
  event: string;
  status: string;
  startDate: string;
  endDate: string;
}

const Table: React.FC<TableProps> = ({
  Data,
  setLoading,
  total,
  setData,
  setTotal,
  perimeter,
  institutionId,
  event,
  status,
  startDate,
  endDate,
}) => {
  let { reportingId } = useParams();

  const [open, setOpen] = useState(false);
  const [modalCnt, setModalCnt] = useState("");
  const [startPage, setStartPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [pageOffset, setPageOffset] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selectedRow, setSelectedRow] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const pageSize = 30;
  const [epidemicFormData, setEpidemicFormData] =
    useState<updateEpidemicTypes>();
  const [nothingToDeclareFormData, setNothingToDeclareFormData] =
    useState<updateNothingToDeclareTypes>();
  const [incidentsFormData, setIncidentsFormData] =
    useState<updateIncidentsInputeTypes>();
  const [healthEventFormData, setHealthEventFormData] =
    useState<updateHealthInputsTypes>();
  const [safetyEventsFormData, setSafetyEventsFormData] =
    useState<updateSafetyTypes>();
  const [foodSafetyFormData, setFoodSafetyFormData] =
    useState<updateFoodsafetyTypes>();

  const hasRole = permit(["admin", "superadmin"]);
  const setSafetyEventsData = (data: any) => {
    data.immediateConsequencesArray = data.immediateConsequences.split("-,-");
    data.immediateCorrectiveActionArray =
      data.immediateCorrectiveAction.split("-,-");
    data.riskRelatedEventArray = data.riskRelatedEvent.split("-,-");

    setSafetyEventsFormData(data);
  };

  const setHealthEventData = (data: any) => {
    data.riskRelatedEventArray = data.riskRelatedEvent.split("-,-");
    data.riskRelatedEvent = data.riskRelatedEvent.split("-,-");
    setHealthEventFormData(data);
  };

  const setIncidentsData = (data: any) => {
    data.immediateCorrectiveActionsArray =
      data.immediateCorrectiveActions.split("-,-");
    data.riskRelatedEventArray = data.riskRelatedEvent.split("-,-");
    setIncidentsFormData(data);
  };

  const fetchNewData = async (selected: number) => {
    setLoading(true);
    try {
      const res = await getData(
        selected + 1,
        perimeter,
        institutionId,
        event,
        status,
        startDate,
        endDate
      );
      if (res) setLoading(false);
      setData(res.data);
      setTotal(res.total);
    } catch (error) {
      setLoading(false);
    }
  };

  const { t } = useTranslation();

  const handlePageChange = ({ selected }: { selected: number }) => {
    fetchNewData(selected);
    setPageOffset(selected);
    setStartPage(selected * pageSize);
  };

  useEffect(() => {
    setPageCount(Math.ceil(total / pageSize));
  }, [Data, startPage, total]);

  useEffect(() => {
    setStartPage(0);
    setPageCount(Math.ceil(total / pageSize));
  }, [Data, total]);

  const getformsData = useCallback(
    async (reportingId: string, formName: string, formId: number) => {
      setLoading(true);

      try {
        const res = await getFormData(formName, formId);
        const data = { ...res.data, reportingId };

        if (res) {
          switch (formName) {
            case "incidents":
              setIncidentsData(data);
              break;
            case "health-events":
              setHealthEventData(data);
              break;
            case "safety-events":
              setSafetyEventsData(data);
              break;
            case "food-safety-issues":
              setFoodSafetyFormData(data);
              break;
            case "epidemics":
              setEpidemicFormData(data);
              break;
            case "nothing-to-declare":
              setNothingToDeclareFormData(data);
              break;
          }
          setLoading(false);
          setOpen(true);
        }
      } catch (error) {
        setLoading(false);
        setOpen(false);
      }
    },
    [setLoading]
  );

  useEffect(() => {
    if (reportingId) {
      getSingleReporting(reportingId).then(({ data }) => {
        setModalCnt(data.event);
        let formName = "";
        switch (data.event) {
          case "Incidents":
            formName = "incidents";
            break;
          case "Health event":
            formName = "health-events";
            break;
          case "Other safety events":
            formName = "safety-events";
            break;
        }
        getformsData(data._id, formName, data.eventId);
      });
    }
  }, [reportingId, getformsData]);

  const updateRecord = (eventData: any) => {
    if (!eventData._id) {
      eventData._id = selectedRow;
    }
    
    console.log("Updating record with data:", eventData);
    
    // Utiliser l'approche immuable pour éviter les problèmes de mise à jour d'état
    setData(prevData => {
      // Trouver l'enregistrement par eventId d'abord, puis par _id si non trouvé
      let index = prevData.findIndex((record) => record.eventId === eventData.eventId);
      if (index === -1) {
        index = prevData.findIndex((record) => record._id === eventData._id);
      }
      
      // Si l'enregistrement n'est pas trouvé, retourner les données inchangées
      if (index === -1) {
        console.log("Enregistrement non trouvé pour la mise à jour:", eventData);
        return prevData;
      }
      
      // Créer un nouveau tableau avec l'enregistrement mis à jour
      const newData = [...prevData];
      
      // Log pour déboguer avant la mise à jour
      console.log("Avant mise à jour:", newData[index]);
      console.log("Données de mise à jour:", eventData);
      
      // S'assurer que status et securityLevel sont correctement mis à jour
      newData[index] = { 
        ...prevData[index],
        ...eventData,
      };
      
      // S'assurer que ces champs sont explicitement présents et prioritaires
      if (eventData.status !== undefined) {
        newData[index].status = eventData.status;
      }
      
      if (eventData.securityLevel !== undefined) {
        newData[index].securityLevel = eventData.securityLevel;
        
        // Synchroniser le statut avec le niveau de sécurité si nécessaire
        const securityLevel = Number(eventData.securityLevel);
        if (securityLevel >= 3 && newData[index].status !== "Need Validation") {
          newData[index].status = "Need Validation";
        } else if (securityLevel < 3 && newData[index].status !== "Closed") {
          newData[index].status = "Closed";
        }
      }
      
      console.log("Après mise à jour:", newData[index]);
      return newData;
    });
    
    // Incrémenter le déclencheur de rafraîchissement pour forcer l'actualisation des composants enfants
    setRefreshTrigger(prev => prev + 1);
    
    // Actualiser les données du tableau depuis le serveur pour s'assurer que tout est à jour
    fetchNewData(pageOffset);
    
    setOpen(false);
  };

  return (
    <div className="w-full bg-white md:p-16 p-3 overflow-auto ">
      <table className="w-full table-auto ">
        <thead className="font-ABeeZee text-base text-[#949494]">
          <tr>
            <th className="font-normal text-start">
              <Trans i18nKey="Event Date">Event Date</Trans>
            </th>
            <th className="font-normal text-start">
              <Trans i18nKey="Country">Country</Trans>
            </th>
            <th className="font-normal text-start whitespace-nowrap">
              <Trans t={t}>Institution Id</Trans>
            </th>
            <th className="font-normal text-start">
              <Trans i18nKey="Event Type">Event Type</Trans>
            </th>
            <th className="font-normal text-start">
              <Trans i18nKey="Severity level">Severity level</Trans>
            </th>
            <th className="font-normal text-start">
              <Trans i18nKey="Status">Status</Trans>
            </th>
            <th className="font-normal text-start">
              <Trans i18nKey="Edit">Edit</Trans>
            </th>
          </tr>
        </thead>
        <tbody className="font-ABeeZee text-base text-black">
          {Data.length > 0 &&
            Data.map((item) => (
              <tr
                key={Data.indexOf(item)}
                className={selectedRow === item._id ? "bg-gray-200" : ""}
              >
                <td className="whitespace-nowrap py-4 pr-4">
                  {formatDate(item?.eventDate)}
                </td>
                <td className="whitespace-nowrap pr-2">{item.perimeter}</td>
                <td className="whitespace-nowrap pr-2">{item.institutionId}</td>
                <td className="whitespace-nowrap pr-2">{item.event}</td>
                <td className="whitespace-nowrap pr-2">
                  {item.event === "Health event" ? (
                    <HealthEventLevel eventId={item.eventId} forceRefresh={refreshTrigger} />
                  ) : item?.securityLevel ? (
                    <span>
                      <Trans i18nKey="level">Level</Trans> {item?.securityLevel}
                    </span>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="whitespace-nowrap pr-2">
                  <div
                    className={`${
                      item.status === "Closed"
                        ? "text-[#0B8A00] bg-[#23C10A26]"
                        : item.status === "Need Validation"
                        ? "text-white bg-[#FF0000]"
                        : "text-gray-600 bg-gray-300"
                    } w-fit rounded px-2 py-0.5`}
                  >
                    {t(`${item.status}`)}
                  </div>
                </td>
                <td>
                  <button
                    className="cursor-pointer"
                    onClick={() => {
                      setModalCnt(item.event);
                      setSelectedRow(item._id);
                      setSelectedPeriod(item.period);
                      getformsData(
                        item._id,
                        item.event === "Epidemic"
                          ? "epidemics"
                          : item.event === "Incidents"
                          ? "incidents"
                          : item.event === "Health event"
                          ? "health-events"
                          : item.event === "Other safety events"
                          ? "safety-events"
                          : item.event === "Food Safety Issue"
                          ? "food-safety-issues"
                          : item.event === "Nothing to declare"
                          ? "nothing-to-declare"
                          : "",
                        item.eventId
                      );
                    }}
                  >
                    {hasRole ? <EditButton /> : <EyeButton />}
                  </button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      <div className="flex justify-center mt-5">
        {pageCount > 1 && (
          <ReactPaginate
            className="flex gap-5 text-[#949494] font-ABeeZee"
            previousLabel="<"
            nextLabel=">"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakLabel="..."
            breakClassName="page-item"
            breakLinkClassName="page-link"
            pageCount={pageCount}
            marginPagesDisplayed={1}
            pageRangeDisplayed={2}
            onPageChange={handlePageChange}
            containerClassName="pagination"
            activeClassName="text-black"
            forcePage={pageOffset | -1}
          />
        )}
      </div>
      <Modal
        open={open}
        setOpen={setOpen}
        content={
          modalCnt === "Incidents" ? (
            <UpdateIncidents
              {...incidentsFormData!}
              setOpen={setOpen}
              updateRecord={updateRecord}
              selectedPeriod={selectedPeriod}
            />
          ) : modalCnt === "Health event" ? (
            <UpdateHealthEvents
              {...healthEventFormData!}
              setOpen={setOpen}
              updateRecord={updateRecord}
              selectedPeriod={selectedPeriod}
            />
          ) : modalCnt === "Other safety events" ? (
            <UpdateSafetyEvents
              {...safetyEventsFormData!}
              setOpen={setOpen}
              updateRecord={updateRecord}
              selectedPeriod={selectedPeriod}
            />
          ) : modalCnt === "Food Safety Issue" ? (
            <UpdateFoodSafety
              {...foodSafetyFormData!}
              setOpen={setOpen}
              updateRecord={updateRecord}
              selectedPeriod={selectedPeriod}
            />
          ) : modalCnt === "Epidemic" ? (
            <UpdateEpidemic
              {...epidemicFormData!}
              setOpen={setOpen}
              updateRecord={updateRecord}
              selectedPeriod={selectedPeriod}
            />
          ) : modalCnt === "Nothing to declare" ? (
            <UpdateNothingToDeclare
              {...nothingToDeclareFormData!}
              setOpen={setOpen}
              updateRecord={updateRecord}
            />
          ) : (
            <></>
          )
        }
      />
    </div>
  );
};

export default Table;
